import { useMutation } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

export enum ValidationMessageLevel {
  ERROR = 'ERROR',
  MESSAGE = 'MESSAGE',
  WARNING = 'WARNING',
}

export type DonorMismatchData = {
  existingDonorId?: string | null;
  existingRegistrationNumber?: string | null;
  existingPedigreeName?: string | null;
  existingBirthDate?: string | null;
  inputRegistrationNumber?: string | null;
  inputPedigreeName?: string | null;
  inputBirthDate?: string | null;
};

export type ValidationFieldMessage = {
  field: string;
  message: string;
};

export type ValidationInputMessage = {
  index: number;
  field: string;
  message: string;
  level: ValidationMessageLevel;
  suggestions?: string[] | null;
  correctedValue?: string | null;
  mismatchData?: DonorMismatchData | null;
};

export type BulkDonorValidationResponse = {
  fields: ValidationFieldMessage[];
  inputs: ValidationInputMessage[];
};

export type ValidateBulkDonorRequest = {
  inputs: (BulkDonorInputRow | null)[];
};

export type BulkDonorInputRow = {
  accountCode: string;
  yardCode?: string | null;
  donorBreed: string;
  donorNameToMatch: string;
  donorRegistrationNumber?: string | null;
  pedigreeName?: string | null;
  pregnant?: boolean | null;
  birthDate?: string | null;
  programName?: string | null;
  tpi?: number | null;
  nm?: number | null;
  opuProgram?: string | null;
  tattoo?: string | null;
  received?: string | null;
  exportDonor?: boolean | null;
};

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export function useValidateBulkDonors() {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(request: ValidateBulkDonorRequest) {
      const response = await fetch(
        `${getBaseUrl()}/api/v1/bulk-donor/validate`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(token),
          body: JSON.stringify(request),
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as BulkDonorValidationResponse;
    },
  });
}

export function useUpsertBulkDonors() {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(inputs: BulkDonorInputRow[]) {
      const response = await fetch(`${getBaseUrl()}/api/v1/bulk-donor/upsert`, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(token),
        body: JSON.stringify(inputs),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    },
  });
}
